
import { AppBar, Toolbar, Stack, SxProps, Theme, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, Box } from "@mui/material";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { createTheme, styled, useTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import Swipeable from "../swipeable/Swipeable";
import BreadCrumbV2Desktop from "../bread-crumb/BreadCrumbV2Desktop";
import React, { useMemo, useState } from "react";
import BackButton from "../back-button/BackButton";
import { useLocation } from "react-router";
import { getNavigationRoutes } from "../../common/utilities/GetNavigationRoutes";
import { useAvailableViews } from "../../queries/available-views/UseAvailableViews";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { isMobile, isTablet, useMobileOrientation } from 'react-device-detect';
import { ViewOptionStateEnum } from "./ViewOptionStateEnum";
import { routes, UIViewCategory } from "../../routes/routes";
import OrganizationPicker from "../organization-picker/OrganizationPicker";
import { IOrganization } from "../../services/organization-data-service/Models";
import { ContextActionMenuV2 } from "./ContextActionMenuV2";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import GroupPicker from "../group-picker/GroupPicker";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import BreadCrumb from "../bread-crumb/BreadCrumb";
import { ContextConfigurationPicker } from "./ContextConfigurationPicker";
import { getActionMenuItems } from "../action-menu/BaseActions";
import { useAppHeaderContext } from "../../contexts/app-header-context/UseAppHeaderContext";
import { EXECUTE_ACTION_TOPIC, ExecuteActionMsg } from "../../common/models/pub-sub/PubSubTopics";
import { publish } from "pubsub-js";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    // Override media queries injected by theme.mixins.toolbar
    //   '@media all': {
    //     minHeight: 128,
    //   },
}));

export type ContextualAppBarV2Props = {
    viewOptionState?: ViewOptionStateEnum;
    onExecuteAction?: (action: ActionMenuItem) => void;
    getRefreshAction?: () => ActionMenuItem;
    actions?: ActionMenuItem[];
    configurationActions?: ActionMenuItem[];
    hideBreadcrumb?: boolean;
    showOrganizationPicker?: boolean;
    onOrganizationSelected?: (item: IOrganization) => void;
    organizationID?: number;

    showGroupPicker?: boolean;
    onGroupSelected?: (item: INavigationItem) => void;
    groupID?: number;
    hideOptionsAction?: boolean;
    dense?: boolean;
    label?: string;
    viewMaximized?: boolean;

    onOverrideViewOptionsClick?: () => void;
    hideMaximizeViewAction?: boolean;

    sx?: SxProps<Theme>;
    onGoingBack?: () => boolean;
};

export default function ContextualAppBarV2({
    onExecuteAction,
    actions,
    configurationActions,
    viewOptionState,
    hideBreadcrumb,
    showOrganizationPicker,
    onOrganizationSelected,
    organizationID,
    showGroupPicker,
    onGroupSelected,
    groupID,
    hideOptionsAction,
    dense,
    label,
    viewMaximized,
    onOverrideViewOptionsClick,
    hideMaximizeViewAction,
    sx,
    onGoingBack
}: ContextualAppBarV2Props) {


    const [subActionAnchorElements, setSubActionAnchorElements] = useState<HTMLButtonElement[]>([]);        

    const isPhone = isMobile && !isTablet;

    const { isLandscape } = useMobileOrientation();

    const location = useLocation();
    const [appSettings] = useAppSettings();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();

    const {
        appHeaderContextState, 
        //setRefreshActionState,
        //setExportActionState
    } = useAppHeaderContext();


    // const theme = useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    //     const theme = useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    //const theme = useTheme();

    const theme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].contextualAppHeaderV2);
        },
        [appSettings.themeSet,
            isPhone]
    );



    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);

    const { data: availableViews } = useQueryNotifications(useAvailableViews)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const navigationRoutes = getNavigationRoutes(availableViews ?? [], isPhone);
    const currentRoute = navigationRoutes.find(o => o.fullPath === location.pathname);
    const currentParentRoute = currentRoute?.parentViewID !== undefined ? navigationRoutes.find(o => o.viewID === currentRoute?.parentViewID) : undefined;
    const route = routes.find(o => o.fullPath === location.pathname);
    const parentRoute = route?.parentViewID !== undefined ? routes.find(o => o.viewID === route?.parentViewID) : undefined;

    const manualEntryView = availableViews?.find(o => o === UIViewEnum.Device_ManualEntry);

    const title = label ? label :
        route?.viewID === UIViewEnum.Device_GaugeSheet && manualEntryView !== undefined
            ? routes.find(o => o.viewID === UIViewEnum.Device_ManualEntry)?.name
            : currentParentRoute?.name ?? currentRoute?.name ?? parentRoute?.name ?? route?.name;


    const adminView = route?.category === UIViewCategory.AdminGroup || route?.category === UIViewCategory.AdminView;
    const showBackButton = isPhone && (routeContext.contextObject?.objectType !== eLynxObjectTypeEnum.Group || adminView);


    const hideAppBar = isPhone && isLandscape;

    const refreshAction = useMemo(() => {
        const item = getActionMenuItems([BaseActionEnum.Refresh]).at(0);

        if (item && (currentRoute?.viewID || routeContext.contextObject)) {
            // reset disabled state
            item.disabled = !appHeaderContextState.refreshActionEnabled;
        }

        return item;
    },[
        currentRoute?.viewID,
        routeContext.contextObject,
        appHeaderContextState
    ]);

    const refreshActionDisabled = !refreshAction || refreshAction?.disabled;

    const exportAction = useMemo(() => {
        const item = getActionMenuItems([BaseActionEnum.Export]).at(0);

        if (item && (currentRoute?.viewID || routeContext.contextObject)) {
            // reset disabled state
            item.disabled = !appHeaderContextState.exportExcelEnabled && !appHeaderContextState.exportPDFEnabled;
        }

        return item;
    },[
        currentRoute?.viewID,
        routeContext.contextObject,
        appHeaderContextState
    ]);

    const exportActionDisabled = !exportAction || 
                                    exportAction?.disabled || 
                                (!appHeaderContextState.exportExcelEnabled && !appHeaderContextState.exportPDFEnabled);    


    function onRefresh() {
        const refreshAction: ActionMenuItem = {
            label: 'refresh',
            action: BaseActionEnum.Refresh
        };

        onExecuteAction?.(refreshAction);
    }

    function onOrganizationChanged(item: IOrganization) {
        onOrganizationSelected?.(item);
    }

    function onGroupChanged(item: INavigationItem) {
        onGroupSelected?.(item);
    }

    function onRefreshAction() {
        publish(EXECUTE_ACTION_TOPIC, {
            action: BaseActionEnum.Refresh
        } as ExecuteActionMsg);
    }

    function onExportAction(item: ActionMenuItem) {
        publish(EXECUTE_ACTION_TOPIC, {
            action: item.action
        } as ExecuteActionMsg);
    }


    const hideAddToFavoritesAction = !(currentRoute || parentRoute) || dense;

    return (dense ? 
        <Stack
            direction='row'
            sx={{
                justifyContent: 'space-between',
                marginRight: 2,
                ...sx
            }}>


            {/* <Typography
                variant="subtitle1"
                noWrap
                component="div"
                sx={{
                    textTransform: "uppercase",
                    fontWeight: 600,
                    flex: "1 1 auto",
                    flexGrow: 1,
                    alignSelf: 'center',
                    overflow: 'hidden',
                    marginLeft: 1
                }}>
                {title}
            </Typography> */}

            <ContextActionMenuV2
                viewOptionState={viewOptionState}
                onExecuteAction={onExecuteAction}
                actions={actions}
                configurationActions={configurationActions}
                hideAddToFavoritesAction={hideAddToFavoritesAction}
                hideOptionsAction={hideOptionsAction}
                showMaximizeViewAction={!hideMaximizeViewAction}
                maximizeView={viewMaximized}
                onOverrideViewOptionsClick={onOverrideViewOptionsClick}
                dense={dense}
            />

        </Stack> : 
        
        //!isPhone && appSettings.maximizeContent ?
        !isPhone ?

//desktop        
        <Swipeable enablePullToRefresh={true} onRefresh={onRefresh} >
            <ThemeProvider theme={theme}>
            <AppBar position="static" //color='default'
                sx={{
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                    paddingRight: theme.spacing(4),
                    // minHeight: isPhone ? 136 : 68
                    flexDirection: 'row'
                }}>
                {
                    !hideBreadcrumb &&
                    <>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                marginLeft: theme.spacing(3),
                                display: 'flex',
                                flex: '1 1 auto'
                            }}>
                            {navigationV2
                                ? <BreadCrumbV2Desktop />
                                : <BreadCrumb />
                            }
                        </Typography>

                    </>
                }

                {
                    showOrganizationPicker &&
                    <OrganizationPicker
                        organizationID={organizationID ?? userState.user?.baseGroup?.organizationID as number}
                        onSelected={onOrganizationChanged}
                    />
                }

                {
                    showGroupPicker && groupID &&
                    <GroupPicker
                        groupID={groupID}
                        onSelected={onGroupChanged}
                    />
                }

                {!isPhone && hideBreadcrumb &&
                    <Typography
                        variant="body1"
                        noWrap
                        component="div"
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            alignSelf: 'center',
                            fontWeight: 600,
                            justifyContent: showGroupPicker || showOrganizationPicker ? undefined : 'center'
                        }}>
                        {title}
                    </Typography>
                }


                <ContextConfigurationPicker />
                <ContextActionMenuV2
                    viewOptionState={viewOptionState}
                    onExecuteAction={onExecuteAction}
                    actions={actions}
                    configurationActions={configurationActions}
                    hideAddToFavoritesAction={hideAddToFavoritesAction}
                    hideOptionsAction={hideOptionsAction} 
                    dense={dense}
                    />


            </AppBar>
            </ThemeProvider>
        </Swipeable>

        :

// mobile
        // <Collapse 
        //     in={!appSettings.maximizeContent} 
        //     sx={{ minHeight: 'auto!important' }}>
            !hideAppBar ? 
            <Swipeable enablePullToRefresh={true} onRefresh={onRefresh} >

                <AppBar 
                    position="static" 
                    color={isPhone ? 'primary' : 'default'} 
                    sx={{ 
                        paddingBottom: theme.spacing(1), 
                        //minHeight: isPhone ? 136 : 68 
                        minHeight: 68
                    }}>
                    <StyledToolbar>

                        {showBackButton &&
                            <BackButton
                                color={theme.palette.text.primary}
                                onGoingBack={onGoingBack}
                            />
                        }

                        {!offlineState.offline && 
                         <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexGrow: 0,
                            flexShrink: 0,
                            //alignItems: "center",
                            marginTop: 'auto',
                            marginBottom: 'auto',
                            marginLeft: -1
                        }}>

                            {!isPhone && <IconButton size="small"
                                disabled={refreshActionDisabled}
                                aria-label="refresh"
                                color='inherit'
                                
                                sx={{
                                    color: refreshActionDisabled ? 'white!important' : undefined,
                                    opacity: refreshActionDisabled ? .3 : undefined
                                }}
                                onClick={() => {
                                    onRefreshAction();
                                }}
                            >
                                <Tooltip title='Refresh'>
                                    {refreshAction?.icon ? 
                                    <refreshAction.icon />
                                    : <></>}
                                </Tooltip>                                                 
                            </IconButton>}

                            <IconButton
                                id={exportAction?.id}
                                size="small"
                                disabled={exportActionDisabled}
                                aria-label='Export'
                                //edge="end"
                                color='inherit'
                                onClick={(event) => {
                                    setSubActionAnchorElements([...subActionAnchorElements, event.currentTarget]);
                                }}
                                sx={{ 
                                    color: exportActionDisabled ? 'white!important' : undefined,
                                    opacity: exportActionDisabled ? .3 : undefined,
                                }}
                            >
                                    <Tooltip title='Export'>
                                        {exportAction?.icon ? 
                                        <exportAction.icon />
                                        : <></>}
                                </Tooltip>    
                                {/* </Box> */}
                            </IconButton>
                            {exportAction?.subActions &&
                                <Menu
                                    id={`subaction-menu`}
                                    color='textPrimary'
                                    anchorEl={subActionAnchorElements.find(v => v.id === exportAction.id)}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center'
                                    }}
                                    keepMounted
                                    open={Boolean(subActionAnchorElements.find(v => v.id === exportAction.id))}
                                    onClose={() => setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== exportAction.id))}
                                >
                                    {exportAction.subActions.map((item, index) => (
                                        <MenuItem
                                            key={`mi${index}`}
                                            disabled={item.action === BaseActionEnum.Excel ? !appHeaderContextState.exportExcelEnabled : !appHeaderContextState.exportPDFEnabled}
                                            onClick={() => {
                                                setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== exportAction.id));
                                                onExportAction(item);
                                            }}>
                                            {item.icon && 
                                            <ListItemIcon id={`${item.id}`} sx={{ minWidth: theme.spacing(4)}}>
                                                {item.checked && item.checkedIcon ? 
                                                    <item.checkedIcon /> : 
                                                        <item.icon />
                                                }
                                            </ListItemIcon>
                                            }
                                            <Typography variant='inherit'>
                                                {item.label}
                                            </Typography>
                                        </MenuItem>

                                    ))}
                                </Menu>
                                }

                        </Box>
                        }

                        <ContextActionMenuV2
                            viewOptionState={viewOptionState}
                            onExecuteAction={onExecuteAction}
                            actions={actions}
                            configurationActions={configurationActions}
                            hideAddToFavoritesAction={hideAddToFavoritesAction}
                            hideOptionsAction={hideOptionsAction} 
                        />
                        

                    </StyledToolbar>

                    {/* {isPhone && !appSettings.maximizeContent && <Typography
                        variant="h5"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            marginLeft: theme.spacing(7),
                            marginRight: theme.spacing(3)
                        }}
                    >
                        {title}
                    </Typography>
                    } */}

                    {
                        !hideBreadcrumb &&
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                marginLeft: isPhone ? theme.spacing(6) : theme.spacing(3),
                                marginRight: isPhone ? 0 : theme.spacing(3),
                            }}>
                            {navigationV2 && !isPhone
                                ? <BreadCrumbV2Desktop />
                                : !appSettings.maximizeContent && <BreadCrumb />
                            }
                        </Typography>
                    }


                    {
                        showOrganizationPicker &&
                        <OrganizationPicker
                            organizationID={organizationID ?? userState.user?.baseGroup?.organizationID as number}
                            onSelected={(item) => onOrganizationSelected && onOrganizationSelected(item)}
                        />
                    }

                    {
                        showGroupPicker && groupID &&
                        <GroupPicker
                            groupID={groupID}
                            onSelected={onGroupChanged}
                        />}


                </AppBar>
            </Swipeable> : null
        // </Collapse>
    );
}
